import type { CartItemDTO, SubItemPromo } from '@t/api.types';
import type { Plan, PlanAddOn, Channel, CategoryType } from '@t/productOffering.types';
import type { ParsedEquipment } from '@t/equipmentOffering.types';

// ---------------------------------------------------------------------------
// Price helpers
// ---------------------------------------------------------------------------

export const getRecurringPrice = (item: CartItemDTO): number | null => {
  const prices = item.itemTotalPrice ?? item.itemPrice ?? [];
  const recurring =
    prices.find((p) => p.priceType === 'recurringCharge' || p.priceType === 'recurring') ??
    prices.find(
      (p) =>
        p.priceType !== 'discount' &&
        p.priceType !== 'oneTime' &&
        p.priceType !== 'oneTimeCharge' &&
        p.recurringChargePeriod &&
        p.recurringChargePeriod !== 'oneTime',
    );
  return recurring?.price?.taxIncludedAmount?.amount ?? recurring?.price?.dutyFreeAmount?.amount ?? null;
};

export const formatPrice = (amount: number | null | undefined): string => {
  if (amount == null) return '--';
  return Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`;
};

/** Extract any price from a cart item (recurring first, then any available). */
export const getAnyPrice = (item: CartItemDTO): number | null => {
  const prices = item.itemTotalPrice ?? item.itemPrice ?? [];
  const first = prices[0];
  return first?.price?.taxIncludedAmount?.amount ?? first?.price?.dutyFreeAmount?.amount ?? null;
};

// ---------------------------------------------------------------------------
// Description parser
// ---------------------------------------------------------------------------

/** Parse discountTag / discountMessage from productOffering.description JSON. */
export const parseOfferingDescription = (description?: string): { discountTag?: string; discountMessage?: string } => {
  if (!description) return {};
  try {
    const parsed = JSON.parse(description) as Record<string, unknown>;
    return {
      discountTag: typeof parsed.discountTag === 'string' ? parsed.discountTag : undefined,
      discountMessage: typeof parsed.discountMessage === 'string' ? parsed.discountMessage : undefined,
    };
  } catch {
    return {};
  }
};

// ---------------------------------------------------------------------------
// Promo helpers
// ---------------------------------------------------------------------------

export type { SubItemPromo };

export const getSubItemPromo = (child: CartItemDTO): SubItemPromo | null => {
  const prices = child.itemTotalPrice ?? child.itemPrice ?? [];

  // Strategy 1: priceAlteration nested on a price entry
  for (const p of prices) {
    for (const alt of p.priceAlteration ?? []) {
      const altAmount = alt.price?.dutyFreeAmount?.amount ?? alt.price?.taxIncludedAmount?.amount ?? 0;
      if (altAmount !== 0) {
        return {
          tag: alt.name ?? 'Promo',
          savingsMessage: alt.description ?? `Save ${formatPrice(Math.abs(altAmount))}`,
        };
      }
    }
  }

  // Strategy 2: sibling CartPriceDTO with priceType === 'discount' (used by top-level plan
  // items and add-on children where the backend emits a separate discount price entry)
  const discountEntry = prices.find((p) => p.priceType === 'discount');
  if (discountEntry) {
    const discountAmount =
      discountEntry.price?.dutyFreeAmount?.amount ?? discountEntry.price?.taxIncludedAmount?.amount ?? 0;
    if (discountAmount !== 0) {
      return {
        tag: discountEntry.name ?? 'Promo',
        savingsMessage: discountEntry.description ?? `Save ${formatPrice(Math.abs(discountAmount))}`,
      };
    }
  }

  return null;
};

// ---------------------------------------------------------------------------
// Classification helpers
// ---------------------------------------------------------------------------

export const inferServiceType = (item: CartItemDTO, plan?: Plan | null): string => {
  if (plan) {
    const mapping: Record<string, string> = { Internet: 'Internet Plan', Video: 'TV Plan', Phone: 'Phone Plan' };
    return mapping[plan.typeName] ?? plan.typeName;
  }
  const name = (item.productOffering?.name ?? '').toLowerCase();
  if (name.includes('internet') || name.includes('wifi')) return 'Internet Plan';
  if (name.includes('tv') || name.includes('video')) return 'TV Plan';
  if (name.includes('phone')) return 'Phone Plan';
  return '';
};

export const findMatchingAddOn = (child: CartItemDTO, planAddOns: PlanAddOn[]): PlanAddOn | undefined =>
  planAddOns.find((a) => a.id === child.productOffering?.id || a.name === child.productOffering?.name);

// ---------------------------------------------------------------------------
// Categorization
// ---------------------------------------------------------------------------

export interface CategorizedChildren {
  addOns: CartItemDTO[];
  products: CartItemDTO[];
  channels: CartItemDTO[];
  themePackages: CartItemDTO[];
  /** Mandatory non-equipment plan members (bundle components, e.g. Internet 100 inside Double Play). */
  mandatoryMembers: CartItemDTO[];
}

export const categorizeChildren = (
  children: CartItemDTO[],
  planAddOns: PlanAddOn[],
  planCategory?: CategoryType
): CategorizedChildren => {
  const result: CategorizedChildren = {
    addOns: [],
    products: [],
    channels: [],
    themePackages: [],
    mandatoryMembers: [],
  };

  for (const child of children) {
    // Equipment items — check both the cart item type and the productOffering type
    // (TMF flow sets type on productOffering, not on the cart item itself)
    if (child.type === 'equipment' || child.productOffering?.type === 'equipment') {
      result.products.push(child);
      continue;
    }

    const matchedAddOn = findMatchingAddOn(child, planAddOns);

    if (matchedAddOn) {
      // Mirrors classifyAddOn logic from addOnMapper:
      // theme packages have a channels array, individual channels are Video add-ons without channels
      if (matchedAddOn.channels && matchedAddOn.channels.length > 0) {
        result.themePackages.push(child);
      } else if (planCategory === 'Video') {
        result.channels.push(child);
      } else {
        result.addOns.push(child);
      }
    } else if (child.isMandatory) {
      // Mandatory bundled plan members (e.g. Fiber Internet 100, Premium TV inside a bundle)
      result.mandatoryMembers.push(child);
    }
  }

  return result;
};

// ---------------------------------------------------------------------------
// Display types (narrow projections for rendering)
// ---------------------------------------------------------------------------

export interface ThemePackageDisplay {
  name: string;
  price: number | null;
  channels: Channel[];
  discountTag?: string;
  discountMessage?: string;
  promo?: SubItemPromo | null;
}

export interface IndividualChannelDisplay {
  name: string;
  price: number | null;
  imageUrl: string;
  descriptionImage?: string;
  description?: string;
  promo?: SubItemPromo | null;
}

export interface ServiceAddOnDisplay {
  childItemId: string;
  offeringId: string;
  name: string;
  price: number | null;
  quantity: number;
  supportsQuantity: boolean;
  description?: string;
  discountTag?: string;
  discountMessage?: string;
  promo?: SubItemPromo | null;
}

export interface HardwareItemDisplay {
  key: string;
  name: string;
  price: number | null;
  isFree: boolean;
  quantity: number;
  supportsQuantity: boolean;
  unitPrice: number | null;
  imageUrl: string;
  description?: string;
  discountTag?: string;
  discountMessage?: string;
  promo?: SubItemPromo | null;
  childItemId: string;
  offeringId: string;
}

export const buildAddOnDisplayData = (
  categorized: CategorizedChildren,
  planAddOns: PlanAddOn[]
): {
  themePackages: ThemePackageDisplay[];
  individualChannels: IndividualChannelDisplay[];
  serviceAddOns: ServiceAddOnDisplay[];
} => {
  const themePackages: ThemePackageDisplay[] = categorized.themePackages.map((child) => {
    const matched = findMatchingAddOn(child, planAddOns);
    const desc = parseOfferingDescription(child.productOffering?.description);
    return {
      name: child.productOffering?.name ?? 'Theme Package',
      price: getRecurringPrice(child) ?? matched?.price ?? null,
      channels: matched?.channels ?? [],
      discountTag: desc.discountTag,
      discountMessage: desc.discountMessage,
      promo: getSubItemPromo(child),
    };
  });

  const individualChannels: IndividualChannelDisplay[] = categorized.channels.map((child) => {
    const matched = findMatchingAddOn(child, planAddOns);
    return {
      name: child.productOffering?.name ?? 'Channel',
      price: getRecurringPrice(child) ?? matched?.price ?? null,
      imageUrl: matched?.imageUrl ?? '',
      descriptionImage: matched?.descriptionImage,
      description: matched?.description,
      promo: getSubItemPromo(child),
    };
  });

  // Group add-on children by offering ID and sum quantities,
  // since the cart creates separate items for each unit.
  const addOnGroups = new Map<string, ServiceAddOnDisplay>();
  for (const child of categorized.addOns) {
    const offeringId = child.productOffering?.id ?? '';
    const existing = addOnGroups.get(offeringId);
    if (existing) {
      existing.quantity += child.quantity ?? 1;
    } else {
      const matched = findMatchingAddOn(child, planAddOns);
      const desc = parseOfferingDescription(child.productOffering?.description);
      addOnGroups.set(offeringId, {
        childItemId: child.id ?? '',
        offeringId,
        name: child.productOffering?.name ?? 'Add-On',
        price: getRecurringPrice(child) ?? matched?.price ?? null,
        quantity: child.quantity ?? 1,
        supportsQuantity: matched?.supportsQuantity ?? false,
        description: child.product?.description || matched?.description || undefined,
        discountTag: desc.discountTag,
        discountMessage: desc.discountMessage,
        promo: getSubItemPromo(child),
      });
    }
  }
  const serviceAddOns = [...addOnGroups.values()].toSorted((a, b) => a.offeringId.localeCompare(b.offeringId));

  return { themePackages, individualChannels, serviceAddOns };
};

export const buildHardwareDisplayData = (
  items: CartItemDTO[],
  equipmentList: ParsedEquipment[] = []
): HardwareItemDisplay[] => {
  const findEquipment = (child: CartItemDTO): ParsedEquipment | undefined =>
    equipmentList.find((e) => e.id === child.productOffering?.id || e.name === child.productOffering?.name);

  // Group equipment children by offering ID and sum quantities,
  // since the cart creates separate items for each unit.
  const hardwareGroups = new Map<string, HardwareItemDisplay>();
  for (const child of items) {
    const offeringId = child.productOffering?.id ?? '';
    const existing = hardwareGroups.get(offeringId);
    if (existing) {
      existing.quantity += child.quantity ?? 1;
    } else {
      const equipment = findEquipment(child);
      const desc = parseOfferingDescription(child.productOffering?.description);
      const childPrice = getRecurringPrice(child) ?? getAnyPrice(child) ?? equipment?.price ?? null;
      const isFree = childPrice === 0;
      const quantity = child.quantity ?? 1;
      const name = equipment?.name ?? child.productOffering?.name ?? 'Equipment';

      hardwareGroups.set(offeringId, {
        key: child.id ?? offeringId,
        name,
        price: childPrice,
        isFree,
        quantity,
        supportsQuantity: equipment?.supportsQuantity ?? false,
        unitPrice: equipment?.price ?? childPrice,
        imageUrl: equipment?.imageUrl ?? '',
        description: equipment?.description,
        discountTag: desc.discountTag,
        discountMessage: desc.discountMessage,
        promo: getSubItemPromo(child),
        childItemId: child.id ?? '',
        offeringId,
      });
    }
  }
  return [...hardwareGroups.values()].toSorted((a, b) => a.key.localeCompare(b.key));
};
